import { useCallback, useEffect, useState, useMemo, memo } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles, Typography } from '@material-ui/core';
import CustomCheckbox from '../../shared/customCheckbox';
import UserAvatar from '../../shared/userAvatar';
import GridTable from '../../shared/gridTable';
import Button from '../../shared/customButton';
import SelectField from '../../shared/selectField';
import Search from '../../shared/search';
import BulkEditDialog from '../../shared/bulkEditDialog';
import ConditionalTooltip from '../../shared/conditionalTooltip';
import Filters from '../../shared/filters';
import DotsMenu from '../../shared/dotsMenu';
import AlertDialog from '../../shared/alertDialog';
import NotificationCard from '../../shared/notificationCard';
import TransferEmployeesDialog from '../../shared/transferEmployeesDialog';
import AddUsersDialog from '../../shared/addUsersDialog';
import UserStatus from '../../shared/userStatus';
import CustomFormDrawer from '../../shared/customFormDrawer';
import AssignUsersDialog from '../../shared/assignUsersDialog';
import PageContainer from '../../shared/pageContainer';
import EmploymentTerminationDateDialog from '../../shared/employmentTerminationDateDialog';
import {
  PERMISSION_CODES,
  PERMISSION_SCOPES,
} from '../../../constants/permissions';
import { PLACEMENTS } from '../../../constants/tags';
import { SYSTEM_ROLES, SYSTEM_ROLE_OPTIONS } from '../../../constants/roles';
import {
  BULK_ACTION_OPTIONS,
  BULK_ACTION_OPTIONS_VALUES,
} from '../../../constants/bulkActionOptions';
import {
  API_ORG_CHART,
  api_user,
  API_USERS_BASIC,
  API_USERS,
  API_USERS_MULTIPLE,
  API_VALIDATE_EMAILS,
  api_user_employment_info,
  api_user_personal_info,
  API_USER_FIELD_CONFIG,
} from '../../../constants/apiRoutes';
import { USER_STATUSES } from '../../../constants/statuses';
import {
  PARAMS,
  PEOPLE_DEFAULT_PARAMS,
  PAGE_WHITELISTED_PARAMS,
  APP_PAGES,
} from '../../../constants/pages';
import {
  INITIAL_SORT,
  ADD_USER_OPTION_KEYS,
  USER_EMPLOYMENT_INFORMATION,
  USER_PERSONAL_INFO_KEYS,
  PEOPLE_DEFAULT_ORDERING,
  USER_VIEW_OPTIONS,
  USER_BASIC_PERSONAL_INFORMATION,
  USER_EMPLOYMENT_INFO_KEYS,
  LANGUAGES,
  EXTERNAL_USER_INFO_KEYS,
  GENDER_OPTIONS,
} from '../../../constants/people';
import { sticky } from '../../../constants/helperCssRules';
import { jobTitlesSelector } from '../../../store/selectors/jobTitlesSelector';
import { levelsAndTracksSelector } from '../../../store/selectors/levelsAndTracksSelector';
import { divisionsAndDepartmentsSelector } from '../../../store/selectors/divisionsAndDepartmentsSelector';
import { tagsSelector } from '../../../store/selectors/tagsSelector';
import { peopleSelector } from '../../../store/selectors/peopleSelector';
import { getPeople, clearPeople } from '../../../store/modules/people';
import {
  getPageQuickFilters,
  getTagsCategories,
  clearPageQuickFilters,
  clearTagsCategories,
} from '../../../store/modules/tags';
import { getJobTitles, clearJobTitles } from '../../../store/modules/jobTitles';
import {
  getLevelsAndTracks,
  clearLevelsAndTracks,
} from '../../../store/modules/levelsAndTracks';
import {
  getDivisionsAndDepartments,
  clearDivisionsAndDepartments,
} from '../../../store/modules/divisionsAndDepartments';
import { getOrganizationSettings } from '../../../store/modules/organizationSettings';
import { updateCurrentUserInfo } from '../../../store/modules/auth';
import {
  getAuthReporters,
  getAuthScopedUsers,
} from '../../../store/modules/accessControl';
import {
  isActiveUserView,
  isDeactivatedUserView,
  isExternalUserView,
} from '../../../utility/person';
import { prepareCategoriesWithTags } from '../../../utility/tagUtils';
import {
  parseDuplicateParameters,
  showSuccessMessage,
  hasNextPage,
  prepareTracksWithLevels,
  parseQueryParams,
  prepareFrameworksWithTracks,
  prepareDivisionsWithDepartments,
} from '../../../utility/uiUtils';
import {
  isArray,
  isArrayEmpty,
  getUnionOfTwoArrays,
  trimString,
  isObjectEmpty,
  getItemById,
} from '../../../utility/helpers';
import http from '../../../utility/http';
import {
  EVENT_ACTION_TYPES,
  tagManagerDataLayer,
} from '../../../utility/tagManager';
import {
  hasAuthPermissionCheck,
  hasScopedAuthPermissionCheck,
  hasOnlySelfScopePermissionCheck,
  hasRequiredScopeCheck,
} from '../../../utility/permissions';
import {
  prepareUserStatuses,
  prepareInitialUserData,
  prepareTimezones,
  prepareEditPersonalInformationFields,
  prepareEditEmploymentInformationFields,
  prepareInitialEmploymentUserData,
  getFieldSettingsMap,
} from '../../../utility/people';
import { useTranslations } from '../../../utility/useTranslations';
import { prepareRoles, prepareSystemRoleFilters } from '../../../utility/roles';
import {
  preparePeoplePageFilters,
  preparePeoplePageTableHeaders,
  preparePeoplePageHeaderActions,
  preparePeoplePageTableData,
  preparePeoplePagePreHeaderActions,
  prepareManagableUsers,
  prepareBulkActionOptions,
  prepareCheckboxTooltip,
  prepareAddUserOptions,
  prepareValidImportedUsers,
  prepareAddUsersFields,
  prepareAddUsersInitialValues,
  prepareAddUsersInfo,
  prepareUsersForSave,
  prepareUserViewOptions,
  prepareWhitelistedFilters,
  prepareViewDescription,
  prepareMenuItems,
  prepareInitialExternalUserData,
  isUserSelectedCheck,
  isDisabledUserSelectCheck,
  areAllUsersSelectedCheck,
} from './config';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    stickyHeader: {
      ...sticky(primary.white, 105, 1000),
    },
    description: {
      padding: spacing(8, 0, 6, 0),
    },
    search: { width: 300 },
    addButton: {
      marginLeft: spacing(2),
      width: 'max-content',
    },
    table: {
      '& .grid-table-header': {
        ...sticky(primary.bluish8, 235),
      },
      '& .name-header-cell': {
        paddingLeft: spacing(10),

        [breakpoints.up('xLg')]: {
          paddingLeft: spacing(12),
        },
      },
      '& .actions-dots-menu': {
        padding: spacing(3, 1),
        overflow: 'visible',

        [breakpoints.up('xLg')]: {
          padding: spacing(3),
        },
      },
    },
    userLabel: {
      fontFamily: 'ProximaNova-Bold',
      fontSize: 16,
      lineHeight: '24px',
    },
    bulkEditAndFiltersWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    bulkEditAndFiltersItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing(4),
    },
    bulkEdit: { height: 35 },
    selectBulkAction: {
      minWidth: 118,
      marginRight: spacing(2),
    },
    bulkActionMenu: {
      minWidth: 210,
      maxHeight: '338px',
    },
    bulkActionMenuList: {
      maxHeight: '304px !important',
    },
    tooltipText: { margin: 0 },
    checkbox: {
      marginLeft: 0,
      minWidth: 24,
    },
    actionButton: {
      marginLeft: spacing(2),
      width: 225,
    },
    addUsersTable: {
      boxSizing: 'border-box',
      padding: spacing(0, 0.25, 0.25, 0.25),
    },
    switchLabel: {
      fontSize: 14,
    },
    currency: {
      marginRight: spacing(2.75),
    },
  })
);

const {
  PAGE,
  SEARCH,
  STATUS,
  ROLE,
  JOB_TITLE,
  TAGS,
  TRACK,
  LEVEL,
  DIVISION,
  DEPARTMENT,
  SCOPE,
  REPORT_TO,
  ORDERING,
  EXCLUDE_ROLE,
  EXCLUDE_STATUS,
  AVAILABLE_MANAGERS,
  WITHOUT_ACTION_PLANS,
} = PARAMS;
const { MANUAL, IMPORT, ASSOCIATE } = ADD_USER_OPTION_KEYS;

const DEFAULT_FILTERS = {
  [ROLE]: [],
  [STATUS]: [],
  [SCOPE]: [],
  [JOB_TITLE]: [],
  [TRACK]: [],
  [LEVEL]: [],
  [DIVISION]: [],
  [DEPARTMENT]: [],
  [TAGS]: [],
  [WITHOUT_ACTION_PLANS]: [],
};

const PeoplePage = ({
  location,
  auth,
  accessControl,
  navigate,
  dispatch,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.PEOPLE);

  const { people } = useSelector(peopleSelector);
  const { jobTitles } = useSelector(jobTitlesSelector);
  const { frameworks, tracks } = useSelector(levelsAndTracksSelector);
  const { divisions, departments } = useSelector(
    divisionsAndDepartmentsSelector
  );
  const { categories, pageQuickFilters } = useSelector(tagsSelector);

  const [page, setPage] = useState(PEOPLE_DEFAULT_PARAMS[PAGE]);
  const [ordering, setOrdering] = useState(INITIAL_SORT);
  const [search, setSearch] = useState('');
  const [selectedFilters, setSelectedFilters] = useState(DEFAULT_FILTERS);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [checkedUsers, setCheckedUsers] = useState([]);
  const [reportToOptions, setReportToOptions] = useState([]);
  const [usersToTransfer, setUsersToTransfer] = useState([]);
  const [importedUsers, setImportedUsers] = useState([]);
  const [availableManagers, setAvailableManagers] = useState([]);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [selectedBulkAction, setSelectedBulkAction] = useState({});
  const [organizationMentor, setOrganizationMentor] = useState(null);
  const [fieldsSettings, setFieldsSettings] = useState([]);
  const [isTransferUsersDialogOpened, setIsTransferUsersDialogOpened] =
    useState(false);
  const [
    isEditPersonalInformationDialogOpened,
    setIsEditPersonalInformationDialogOpened,
  ] = useState(false);
  const [
    isEditExternalUserInfoDialogOpened,
    setIsEditExternalUserInfoDialogOpened,
  ] = useState(false);
  const [
    isEditEmploymentInformationDialogOpened,
    setIsEditEmploymentInformationDialogOpened,
  ] = useState(false);
  const [isAddUsersDialogOpened, setIsAddUsersDialogOpened] = useState(false);
  const [isAddUsersDrawerOpened, setIsAddUsersDrawerOpened] = useState(false);
  const [user, setUser] = useState({});
  const [selectedAddUsersOption, setSelectedAddUsersOption] = useState(MANUAL);
  const [csvImport, setCsvImport] = useState({
    validUsers: [],
  });
  const [selectedView, setSelectedView] = useState(USER_VIEW_OPTIONS[0].value);
  const [isInviteDialogOpened, setIsInviteDialogOpened] = useState(false);
  const [isReinviteDialogOpened, setIsReinviteDialogOpened] = useState(false);
  const [isRevokeAccessDialogOpened, setIsRevokeAccessDialogOpened] =
    useState(false);
  const [isDeactivateDialogOpened, setIsDeactivateDialogOpened] =
    useState(false);
  const [isReactivateDialogOpened, setIsReactivateDialogOpened] =
    useState(false);
  const [isDeleteDialogOpened, setIsDeleteDialogOpened] = useState(false);
  const [isBulkEditDialogOpened, setIsBulkEditDialogOpened] = useState(false);
  const { directReportsSet, indirectReportsSet, customEmployeesScopeSet } =
    accessControl;

  const [, authUserAdditionalRole] = auth.roles;
  const canAdd = hasAuthPermissionCheck(
    PERMISSION_CODES.PROFILES_CREATE,
    accessControl
  );
  const canEdit = hasAuthPermissionCheck(
    PERMISSION_CODES.PROFILES_EDIT,
    accessControl
  );
  const canDelete = hasAuthPermissionCheck(
    PERMISSION_CODES.PROFILES_DELETE,
    accessControl
  );
  const canManageOnlySelf = hasOnlySelfScopePermissionCheck(
    PERMISSION_CODES.PROFILES_EDIT,
    accessControl
  );
  const hasGlobalEditScope = hasRequiredScopeCheck(
    PERMISSION_CODES.PROFILES_EDIT,
    PERMISSION_SCOPES.GLOBAL,
    accessControl
  );
  const hasCustomEditScope = hasRequiredScopeCheck(
    PERMISSION_CODES.PROFILES_EDIT,
    PERMISSION_SCOPES.CUSTOM,
    accessControl
  );
  const hasGlobalDeleteScope = hasRequiredScopeCheck(
    PERMISSION_CODES.PROFILES_DELETE,
    PERMISSION_SCOPES.GLOBAL,
    accessControl
  );
  const hasCustomDeleteScope = hasRequiredScopeCheck(
    PERMISSION_CODES.PROFILES_DELETE,
    PERMISSION_SCOPES.CUSTOM,
    accessControl
  );
  const isOwnProfile = auth.id === user.id;
  const userRoles = prepareRoles(
    translations.systemRoles,
    SYSTEM_ROLE_OPTIONS,
    selectedAddUsersOption !== ASSOCIATE
  );

  const pageFilters = useMemo(
    () =>
      preparePeoplePageFilters(
        translations,
        jobTitles,
        tracks,
        divisions,
        departments,
        categories,
        prepareSystemRoleFilters(
          prepareRoles(translations.systemRoles, SYSTEM_ROLE_OPTIONS, true)
        ),
        true,
        pageQuickFilters,
        selectedView
      ),
    [
      translations,
      jobTitles,
      tracks,
      divisions,
      departments,
      categories,
      pageQuickFilters,
      selectedView,
    ]
  );
  const userStatuses = prepareUserStatuses(translations.userStatuses.statuses);
  const userViewOptions = prepareUserViewOptions(translations.userViewOptions);
  const isUserImport = selectedAddUsersOption === IMPORT;
  const isAddUsersDisabled = isUserImport && isArrayEmpty(csvImport.validUsers);
  const isExternalUsersView = isExternalUserView(selectedView);
  const isActiveUsersView = isActiveUserView(selectedView);
  const isDeactUsersView = isDeactivatedUserView(selectedView);
  const description = prepareViewDescription(selectedView, translations);
  const headerActions = preparePeoplePageHeaderActions(
    translations.columns,
    canEdit,
    hasGlobalEditScope,
    hasGlobalDeleteScope,
    hasCustomEditScope,
    hasCustomDeleteScope,
    canDelete,
    isExternalUsersView,
    isDeactUsersView
  );
  const headers = preparePeoplePageTableHeaders(
    translations.columns,
    isExternalUsersView
  );
  const levelingFrameworks = prepareFrameworksWithTracks(frameworks);
  const levelingTracks = prepareTracksWithLevels(tracks);
  const divisionsWithDepartments = prepareDivisionsWithDepartments(divisions);
  const categoriesWithTags = prepareCategoriesWithTags(categories);
  const bulkActionOptions = prepareBulkActionOptions(
    translations.selectBulkActions
  );
  const isBulkEditDisabled = isArrayEmpty(checkedUsers) || !isActiveUsersView;
  const bulkIdleUsers =
    checkedUsers?.filter(
      u => u.status === USER_STATUSES.ACTIVE_WITHOUT_ACCESS
    ) || [];
  const timezones = prepareTimezones();

  const canViewSensitiveDataCheck = selectedUser =>
    hasScopedAuthPermissionCheck(
      PERMISSION_CODES.PROFILES_SENSITIVE_DATA_VIEW,
      accessControl,
      auth.id,
      selectedUser.id
    );

  const getUsersDirectReports = async personId => {
    const { data } = await http.get(API_USERS_BASIC, {
      params: {
        ...PEOPLE_DEFAULT_ORDERING,
        [REPORT_TO]: personId,
      },
    });

    return data;
  };

  const handlePageChange = ({
    appliedFilters,
    newPage,
    selectedUserView,
    newOrdering,
    newSearch = '',
    isLoadMore = false,
  } = {}) => {
    const isExtUsersView = isExternalUserView(selectedUserView);
    const isDeactivatedUsersView = isDeactivatedUserView(selectedUserView);
    const viewsFilters = isDeactivatedUsersView
      ? { [STATUS]: [USER_STATUSES.DEACTIVATED] }
      : {
          [EXCLUDE_ROLE]: [SYSTEM_ROLES.ASSOCIATE],
          [EXCLUDE_STATUS]: [USER_STATUSES.DEACTIVATED],
        };
    if (isDeactivatedUsersView && viewsFilters[EXCLUDE_ROLE]) {
      delete viewsFilters[EXCLUDE_ROLE];
      delete viewsFilters[EXCLUDE_STATUS];
    }

    const addedParams = isExtUsersView
      ? { [ROLE]: [SYSTEM_ROLES.ASSOCIATE], [STATUS]: [], [SCOPE]: [] }
      : {
          ...viewsFilters,
          ...(appliedFilters[ROLE].includes(SYSTEM_ROLES.ASSOCIATE)
            ? { [ROLE]: [] }
            : {}),
          ...(appliedFilters[STATUS].includes(USER_STATUSES.DEACTIVATED) &&
          !isDeactivatedUsersView
            ? { [STATUS]: [] }
            : {}),
          ...(isDeactivatedUsersView ? { [SCOPE]: [] } : {}),
        };

    const params = {
      page: newPage,
      [ORDERING]: newOrdering.sortKey,
      ...(isExtUsersView ? {} : appliedFilters),
      ...addedParams,
      ...(newSearch ? { search: newSearch } : {}),
    };

    return getPeople(dispatch, isLoadMore, params).then(data => {
      setSelectAllChecked(
        areAllUsersSelectedCheck(
          checkedUsers,
          data.results,
          auth,
          accessControl
        )
      );
    });
  };

  const getFilterParams = (params, view) => {
    const isDeactivatedUsersView = isDeactivatedUserView(view);

    let paramsRole = params[ROLE]
      ? [...(isArray(params[ROLE]) ? params[ROLE] : [params[ROLE]])]
      : [];
    paramsRole = isExternalUsersView ? selectedFilters[ROLE] : paramsRole;

    let paramsStatus = params[STATUS]
      ? [...(isArray(params[STATUS]) ? params[STATUS] : [params[STATUS]])]
      : [];
    paramsStatus = isDeactivatedUsersView
      ? selectedFilters[STATUS]
      : paramsStatus;

    const paramsScope = params[SCOPE]
      ? [...(isArray(params[SCOPE]) ? params[SCOPE] : [params[SCOPE]])]
      : [];

    return {
      [ROLE]: paramsRole,
      [STATUS]: paramsStatus,
      [SCOPE]: paramsScope,
      [JOB_TITLE]: params[JOB_TITLE]
        ? [
            ...(isArray(params[JOB_TITLE])
              ? params[JOB_TITLE]
              : [params[JOB_TITLE]]),
          ]
        : [],
      [TRACK]: params[TRACK]
        ? [...(isArray(params[TRACK]) ? params[TRACK] : [params[TRACK]])]
        : [],
      [LEVEL]: params[LEVEL]
        ? [...(isArray(params[LEVEL]) ? params[LEVEL] : [params[LEVEL]])]
        : [],
      [DIVISION]: params[DIVISION]
        ? [
            ...(isArray(params[DIVISION])
              ? params[DIVISION]
              : [params[DIVISION]]),
          ]
        : [],
      [DEPARTMENT]: params[DEPARTMENT]
        ? [
            ...(isArray(params[DEPARTMENT])
              ? params[DEPARTMENT]
              : [params[DEPARTMENT]]),
          ]
        : [],
      [TAGS]: params[TAGS]
        ? [...(isArray(params[TAGS]) ? params[TAGS] : [params[TAGS]])]
        : [],
      [WITHOUT_ACTION_PLANS]:
        params[WITHOUT_ACTION_PLANS] !== undefined
          ? [JSON.parse(params[WITHOUT_ACTION_PLANS])]
          : [],
    };
  };

  const getOrganizationMentor = async () => {
    const { data } = await http.get(API_ORG_CHART);
    setOrganizationMentor({
      ...data,
      last_name: translations.organizationUser,
      isCeo: true,
    });
  };

  const getInitialData = useCallback(async () => {
    const params = parseQueryParams(
      location.search,
      PAGE_WHITELISTED_PARAMS.PEOPLE
    );

    const view = location.state?.view || selectedView;

    if (!isObjectEmpty(params)) {
      const [ascending, descending] = params[ORDERING].split('-');
      const isAscending = !!ascending;
      const tempFilters = getFilterParams(params, view);
      const tempOrdering = {
        column: getItemById(
          headers,
          isAscending ? ascending : descending,
          'sortAs'
        )?.rowKey,
        asc: isAscending,
        sortKey: params[ORDERING],
      };
      const initialSearch = params[SEARCH]
        ? decodeURIComponent(params[SEARCH])
        : '';
      setSelectedView(view);
      setSelectedFilters(tempFilters);
      setOrdering(tempOrdering);
      setSearch(initialSearch);

      handlePageChange({
        newPage: page,
        selectedUserView: view,
        appliedFilters: tempFilters,
        newOrdering: tempOrdering,
        newSearch: initialSearch,
      });
      getJobTitles(dispatch);
      getLevelsAndTracks(dispatch);
      getDivisionsAndDepartments(dispatch);
      getTagsCategories(dispatch);
      getPageQuickFilters(dispatch, PLACEMENTS.PEOPLE.id);
      getOrganizationSettings(dispatch);
      await getOrganizationMentor();
      if (canEdit || canManageOnlySelf || hasGlobalEditScope) {
        const { data } = await http.get(API_USER_FIELD_CONFIG);
        setFieldsSettings(getFieldSettingsMap(data));
      }
      setIsInitialLoad(false);
    } else {
      handlePageChange({
        newPage: page,
        appliedFilters: selectedFilters,
        selectedUserView: selectedView,
        newOrdering: ordering,
        newSearch: search,
      });
      getJobTitles(dispatch);
      getLevelsAndTracks(dispatch);
      getDivisionsAndDepartments(dispatch);
      getTagsCategories(dispatch);
      getPageQuickFilters(dispatch, PLACEMENTS.PEOPLE.id);
      getOrganizationSettings(dispatch);
      await getOrganizationMentor();
      if (canEdit || canManageOnlySelf || hasGlobalEditScope) {
        const { data } = await http.get(API_USER_FIELD_CONFIG);
        setFieldsSettings(getFieldSettingsMap(data));
      }
      setIsInitialLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const cleanup = useCallback(() => {
    dispatch(clearPageQuickFilters());
    dispatch(clearPeople());
    dispatch(clearTagsCategories());
    dispatch(clearJobTitles());
    dispatch(clearLevelsAndTracks());
    dispatch(clearDivisionsAndDepartments());
  }, [dispatch]);

  const getUpdatedData = useCallback(async () => {
    const isDeactivatedUsersView = isDeactivatedUserView(selectedView);
    const viewsFilters = isDeactivatedUsersView
      ? { [STATUS]: [USER_STATUSES.DEACTIVATED] }
      : {
          [EXCLUDE_ROLE]: [SYSTEM_ROLES.ASSOCIATE],
          [EXCLUDE_STATUS]: [USER_STATUSES.DEACTIVATED],
        };
    if (isDeactivatedUsersView && viewsFilters[EXCLUDE_ROLE]) {
      delete viewsFilters[EXCLUDE_ROLE];
      delete viewsFilters[EXCLUDE_STATUS];
    }

    const addedParams = isExternalUsersView
      ? { [ROLE]: [SYSTEM_ROLES.ASSOCIATE], [STATUS]: [], [SCOPE]: [] }
      : {
          ...viewsFilters,
          ...(selectedFilters[ROLE].includes(SYSTEM_ROLES.ASSOCIATE)
            ? { [ROLE]: [] }
            : {}),
          ...(selectedFilters[STATUS].includes(USER_STATUSES.DEACTIVATED) &&
          !isDeactivatedUsersView
            ? { [STATUS]: [] }
            : {}),
          ...(isDeactivatedUsersView ? { [SCOPE]: [] } : {}),
        };

    const params = {
      page,
      [ORDERING]: ordering.sortKey,
      ...(isExternalUsersView ? {} : selectedFilters),
      ...addedParams,
      ...(search ? { search } : {}),
    };
    const query = parseDuplicateParameters(params);

    if (decodeURIComponent(location.search) !== `?${query}`) {
      navigate(`/people/?${query}`, {
        replace: true,
        state: { view: selectedView },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedFilters,
    ordering,
    page,
    search,
    selectedView,
    people.results,
    location.search,
  ]);

  useEffect(() => {
    getUpdatedData();
  }, [getUpdatedData]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const onLoadMore = () => {
    const newPage = page + 1;
    setPage(newPage);
    handlePageChange({
      newPage,
      isLoadMore: true,
      selectedUserView: selectedView,
      newOrdering: ordering,
      appliedFilters: selectedFilters,
      newSearch: search,
    });
  };

  const onSort = newOrdering => {
    setPage(PEOPLE_DEFAULT_PARAMS[PAGE]);
    setOrdering(newOrdering);
    handlePageChange({
      newPage: PEOPLE_DEFAULT_PARAMS[PAGE],
      selectedUserView: selectedView,
      appliedFilters: selectedFilters,
      newOrdering,
      newSearch: search,
    });
  };

  const onSearch = text => {
    const searchTerm = trimString(text);

    setPage(PEOPLE_DEFAULT_PARAMS[PAGE]);
    setSearch(searchTerm);
    handlePageChange({
      newPage: PEOPLE_DEFAULT_PARAMS[PAGE],
      appliedFilters: selectedFilters,
      selectedUserView: selectedView,
      newOrdering: ordering,
      newSearch: searchTerm,
    });
  };

  const handleApplyFilters = newFilters => {
    const tempFilters = isObjectEmpty(newFilters)
      ? DEFAULT_FILTERS
      : { ...DEFAULT_FILTERS, ...newFilters };
    setPage(PEOPLE_DEFAULT_PARAMS[PAGE]);
    setSelectedFilters(tempFilters);
    handlePageChange({
      newPage: PEOPLE_DEFAULT_PARAMS[PAGE],
      appliedFilters: tempFilters,
      selectedUserView: selectedView,
      newOrdering: ordering,
      newSearch: search,
    });
  };

  const handleSelectAllChange = checked => {
    const { results } = people;

    const updatedCheckedUsers = checked
      ? getUnionOfTwoArrays(
          checkedUsers,
          prepareManagableUsers(results || [], auth, accessControl),
          'id'
        )
      : [
          ...checkedUsers.filter(
            person => !results.some(x => x.id === person.id)
          ),
        ];
    setCheckedUsers(updatedCheckedUsers);
    setSelectAllChecked(checked);
  };

  const handleUserCheckboxChange = personId => checked => {
    const { results } = people;
    const updatedCheckedUsers = checked
      ? [...checkedUsers, results.find(person => person.id === personId)]
      : checkedUsers.filter(person => person.id !== personId);

    setCheckedUsers(updatedCheckedUsers);
    setSelectAllChecked(
      areAllUsersSelectedCheck(
        updatedCheckedUsers,
        results,
        auth,
        accessControl
      )
    );
  };

  const handleSelectBulkAction = value => {
    const bulkAction = getItemById(BULK_ACTION_OPTIONS, value, 'value');
    setSelectedBulkAction(bulkAction);
    setIsBulkEditDialogOpened(true);
  };

  const onBulkEditClose = () => {
    setSelectedBulkAction({});
    setIsBulkEditDialogOpened(false);
  };

  const onBulkEditSave = async postData => {
    if (isArrayEmpty(postData)) {
      setSelectAllChecked(false);
      setCheckedUsers([]);
      setSelectedBulkAction({});
      setIsBulkEditDialogOpened(false);
    }

    try {
      await http.patch(API_USERS_MULTIPLE, postData);
      showSuccessMessage(
        translations.bulkEditDialog[
          `${selectedBulkAction.translationKey}Success`
        ] || translations.bulkEditDialog.success
      );
      if (
        !hasGlobalEditScope &&
        (selectedBulkAction.value ===
          BULK_ACTION_OPTIONS_VALUES.changeJobTitle ||
          selectedBulkAction.value ===
            BULK_ACTION_OPTIONS_VALUES.changeLevelTrack ||
          selectedBulkAction.value ===
            BULK_ACTION_OPTIONS_VALUES.changeDivisionDepartment ||
          selectedBulkAction.value === BULK_ACTION_OPTIONS_VALUES.applyTags) &&
        postData.some(
          userData =>
            directReportsSet.has(userData.id) ||
            indirectReportsSet.has(userData.id) ||
            customEmployeesScopeSet.has(userData.id)
        )
      ) {
        getAuthScopedUsers(dispatch);
      }
      if (
        !hasGlobalEditScope &&
        selectedBulkAction.value ===
          BULK_ACTION_OPTIONS_VALUES.changeReportingTo &&
        postData.some(
          userData =>
            userData[USER_EMPLOYMENT_INFORMATION.REPORT_TO] === auth.id ||
            directReportsSet.has(userData.id) ||
            indirectReportsSet.has(userData.id)
        )
      ) {
        getAuthReporters(dispatch);
      }
      setPage(PEOPLE_DEFAULT_PARAMS[PAGE]);
      await handlePageChange({
        newPage: PEOPLE_DEFAULT_PARAMS[PAGE],
        appliedFilters: selectedFilters,
        selectedUserView: selectedView,
        newOrdering: ordering,
        newSearch: search,
      });
    } finally {
      setSelectAllChecked(false);
      setCheckedUsers([]);
      setSelectedBulkAction({});
      setIsBulkEditDialogOpened(false);
    }
  };

  const getAvailableManagers = async person => {
    const { data } = await http.get(API_USERS_BASIC, {
      params: {
        ...PEOPLE_DEFAULT_ORDERING,
        [EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
        [EXCLUDE_STATUS]: USER_STATUSES.DEACTIVATED,
        [AVAILABLE_MANAGERS]: person.id,
      },
      paramsSerializer: d => parseDuplicateParameters(d),
    });

    return [organizationMentor].concat(data);
  };

  const handleUserMentorChange =
    (person = {}) =>
    (value = '') => {
      const trimmedValue = trimString(value);

      const params = {
        page: 1,
        page_size: 12,
        ordering: 'first_name',
        search: trimmedValue,
        status: [
          USER_STATUSES.ACTIVE_WITHOUT_ACCESS,
          USER_STATUSES.ACTIVE_INVITED,
          USER_STATUSES.ACTIVE_WITH_ACCESS,
        ],
        role: [SYSTEM_ROLES.ADMIN, SYSTEM_ROLES.MODERATOR, SYSTEM_ROLES.USER],
      };

      if (person?.id) {
        params.available_managers = person?.id;
      }

      if (trimmedValue) {
        return http
          .get(API_USERS, {
            params,
            paramsSerializer: data => parseDuplicateParameters(data),
          })
          .then(({ data }) => {
            setReportToOptions([organizationMentor].concat(data.results));
          });
      }
      return Promise.resolve(setReportToOptions([]));
    };

  const toggleDeleteUserDialog =
    (person = {}) =>
    () => {
      setIsDeleteDialogOpened(prevState => !prevState);
      setUser(person);
    };

  const onDelete = async () => {
    try {
      await http.delete(api_user(user.id));
      showSuccessMessage(translations.deleteUserDialog.deleteSuccess);
    } finally {
      setPage(PEOPLE_DEFAULT_PARAMS[PAGE]);
      setIsDeleteDialogOpened(false);
      setUser({});
      getOrganizationSettings(dispatch);
      handlePageChange({
        newPage: PEOPLE_DEFAULT_PARAMS[PAGE],
        appliedFilters: selectedFilters,
        selectedUserView: selectedView,
        newOrdering: ordering,
        newSearch: search,
      });
    }
  };

  const handleUploadUsers = async ({ data }) => {
    const users = await prepareValidImportedUsers(data, organizationMentor);

    if (!isArrayEmpty(users.validEmails)) {
      return http
        .post(API_VALIDATE_EMAILS, {
          emails: users.validEmails,
        })
        .then(results => {
          const takenEmails = results.data.taken;
          const validUsers = users.validUsers.filter(
            person => !takenEmails.includes(person.email)
          );

          setCsvImport({ ...users, validUsers, existingUsers: takenEmails });
        });
    }

    setCsvImport({ ...users, existingUsers: [] });
  };

  const handleDeleteUpload = () => setCsvImport({ validUsers: [] });

  const handleSelectAddUsersOption = option => () => {
    if (selectedAddUsersOption !== option) {
      setSelectedAddUsersOption(option);
      setCsvImport({ validUsers: [] });
    }
  };

  const handleOpenAddUsers = () => setIsAddUsersDialogOpened(true);

  const handleCloseAddUsers = () => {
    setIsAddUsersDialogOpened(false);
    setSelectedAddUsersOption(MANUAL);
    setCsvImport({ validUsers: [] });
  };

  const onAddUsers = async ({ users }) => {
    const { AddUser } = EVENT_ACTION_TYPES;
    const isEdit = !isArrayEmpty(importedUsers);
    let addedUsers = [];
    const preparedUsers = prepareUsersForSave(
      users,
      isEdit ? importedUsers : []
    );

    if (isEdit) {
      await http.patch(API_USERS_MULTIPLE, preparedUsers);
    } else {
      addedUsers = await http.post(API_USERS_MULTIPLE, preparedUsers);
    }

    if (!hasGlobalEditScope) {
      if (
        preparedUsers.some(
          u =>
            u?.[USER_EMPLOYMENT_INFORMATION.REPORT_TO] === auth.id ||
            directReportsSet.has(u?.[USER_EMPLOYMENT_INFORMATION.REPORT_TO]) ||
            indirectReportsSet.has(u?.[USER_EMPLOYMENT_INFORMATION.REPORT_TO])
        )
      ) {
        getAuthReporters(dispatch);
      }
      if (authUserAdditionalRole) {
        getAuthScopedUsers(dispatch);
      }
    }

    getOrganizationSettings(dispatch);
    tagManagerDataLayer(AddUser.action, AddUser.name, users.length);
    showSuccessMessage(
      isEdit
        ? translations.addUsersTable.success.update
        : translations.addUsersTable.success.add
    );
    setPage(PEOPLE_DEFAULT_PARAMS[PAGE]);
    setUser({});
    handlePageChange({
      newPage: PEOPLE_DEFAULT_PARAMS[PAGE],
      appliedFilters: selectedFilters,
      selectedUserView: selectedView,
      newOrdering: ordering,
      newSearch: search,
    });

    return Promise.resolve(addedUsers);
  };

  const handleConfirmAddUsers = () => {
    if (isUserImport) {
      return onAddUsers({ users: csvImport.validUsers }).then(({ data }) => {
        setIsAddUsersDrawerOpened(true);
        setIsAddUsersDialogOpened(false);
        setImportedUsers(data);
      });
    }
    setIsAddUsersDrawerOpened(true);
    setIsAddUsersDialogOpened(false);
  };

  const handleCloseAddUsersDrawer = () => {
    setIsAddUsersDrawerOpened(false);
    setCsvImport({ validUsers: [] });
    setImportedUsers([]);
    setSelectedAddUsersOption(MANUAL);
  };

  const toggleTransferUsersDialog = (person = {}, personsToTransfer = []) => {
    setIsTransferUsersDialogOpened(prevState => !prevState);
    setUser(person);
    setUsersToTransfer(personsToTransfer);
  };

  const onTransferUsers = async (terminationDate, personsToTransferId) => {
    const userData = {
      [STATUS]: USER_STATUSES.DEACTIVATED,
      [USER_EMPLOYMENT_INFORMATION.EMPLOYMENT_TERMINATION_DATE]:
        terminationDate,
    };

    try {
      await http.patch(api_user(user.id), userData);
      showSuccessMessage(translations.deactivateSuccess);
      if (!isOwnProfile && !hasGlobalEditScope) {
        if (
          personsToTransferId === auth.id ||
          directReportsSet.has(user.id) ||
          indirectReportsSet.has(user.id)
        ) {
          getAuthReporters(dispatch);
        }
        if (customEmployeesScopeSet.has(user.id)) {
          getAuthScopedUsers(dispatch);
        }
      }
    } finally {
      setPage(PEOPLE_DEFAULT_PARAMS[PAGE]);
      handlePageChange({
        newPage: PEOPLE_DEFAULT_PARAMS[PAGE],
        appliedFilters: selectedFilters,
        selectedUserView: selectedView,
        newOrdering: ordering,
        newSearch: search,
      });
    }
  };

  const handleEditExternalUserInfo = async person => {
    setIsEditExternalUserInfoDialogOpened(prevState => !prevState);
    setUser(person);
  };

  const handlePersonalInfoEditOpen = async person => {
    setIsEditPersonalInformationDialogOpened(prevState => !prevState);
    const { data } = await http.get(api_user_personal_info(person.id));
    setUser({ ...data, ...person });
  };

  const handleEmploymentInfoEditOpen = async person => {
    const managers = await getAvailableManagers(person);
    const { data } = await http.get(api_user_employment_info(person.id));

    setIsEditEmploymentInformationDialogOpened(prevState => !prevState);
    setUser({ ...data, ...person });
    setAvailableManagers(managers);
  };

  const handleCloseEditExternalUserInfoSidebar = () => {
    setIsEditExternalUserInfoDialogOpened(false);
    setUser({});
  };

  const handleCloseEditPersonalInformationSidebar = () => {
    setIsEditPersonalInformationDialogOpened(false);
    setUser({});
  };

  const handleCloseEditEmploymentInformationSidebar = () => {
    setIsEditEmploymentInformationDialogOpened(false);
    setAvailableManagers([]);
    setUser({});
  };

  const handleToggleInvite =
    (person = {}) =>
    () => {
      setIsInviteDialogOpened(prevState => !prevState);
      setUser(person);
    };

  const onInvite = async () => {
    try {
      await http.patch(api_user(user.id), {
        [STATUS]: USER_STATUSES.ACTIVE_INVITED,
      });
      showSuccessMessage(translations.inviteUserDialog.inviteSuccess);
    } finally {
      setPage(PEOPLE_DEFAULT_PARAMS[PAGE]);
      setUser({});
      setIsInviteDialogOpened(false);
      handlePageChange({
        newPage: PEOPLE_DEFAULT_PARAMS[PAGE],
        appliedFilters: selectedFilters,
        selectedUserView: selectedView,
        newOrdering: ordering,
        newSearch: search,
      });
    }
  };

  const handleToggleReinvite =
    (person = {}) =>
    () => {
      setIsReinviteDialogOpened(prevState => !prevState);
      setUser(person);
    };

  const onReinvite = async () => {
    try {
      await http.patch(api_user(user.id), {
        [STATUS]: USER_STATUSES.ACTIVE_INVITED,
      });
      showSuccessMessage(translations.reinviteUserDialog.reinviteSuccess);
    } finally {
      setUser({});
      setIsReinviteDialogOpened(false);
    }
  };

  const handleToggleRevokeAccess =
    (person = {}) =>
    () => {
      setIsRevokeAccessDialogOpened(prevState => !prevState);
      setUser(person);
    };

  const onRevokeAccess = async () => {
    try {
      await http.patch(api_user(user.id), {
        [STATUS]: USER_STATUSES.ACTIVE_WITHOUT_ACCESS,
      });
      showSuccessMessage(translations.revokeAccessUserDialog.revokeSuccess);
    } finally {
      setPage(PEOPLE_DEFAULT_PARAMS[PAGE]);
      setUser({});
      setIsRevokeAccessDialogOpened(false);
      handlePageChange({
        newPage: PEOPLE_DEFAULT_PARAMS[PAGE],
        appliedFilters: selectedFilters,
        selectedUserView: selectedView,
        newOrdering: ordering,
        newSearch: search,
      });
    }
  };

  const handleToggleDeactivate =
    (person = {}) =>
    () => {
      setIsDeactivateDialogOpened(prevState => !prevState);
      setUser(person);
    };

  const handleOpenDeactivate = person => async () => {
    const reporters = await getUsersDirectReports(person.id);

    if (!isArrayEmpty(reporters)) {
      return toggleTransferUsersDialog(person, reporters);
    }

    handleToggleDeactivate(person)();
  };

  const onDeactivate = async value => {
    const userData = {
      [STATUS]: USER_STATUSES.DEACTIVATED,
      [USER_EMPLOYMENT_INFORMATION.EMPLOYMENT_TERMINATION_DATE]: value,
    };

    try {
      await http.patch(api_user(user.id), userData);
      showSuccessMessage(
        translations.employmentTerminationDateDialog.successMessage
      );
      if (!isOwnProfile && !hasGlobalEditScope) {
        if (directReportsSet.has(user.id) || indirectReportsSet.has(user.id)) {
          getAuthReporters(dispatch);
        }
        if (customEmployeesScopeSet.has(user.id)) {
          getAuthScopedUsers(dispatch);
        }
      }
    } finally {
      setPage(PEOPLE_DEFAULT_PARAMS[PAGE]);
      setUser({});
      handlePageChange({
        newPage: PEOPLE_DEFAULT_PARAMS[PAGE],
        appliedFilters: selectedFilters,
        selectedUserView: selectedView,
        newOrdering: ordering,
        newSearch: search,
      });
    }
  };

  const handleToggleReactivate =
    (person = {}, managers = []) =>
    () => {
      setIsReactivateDialogOpened(prevState => !prevState);
      setUser(person);
      setAvailableManagers(managers);
    };

  const handleOpenReactivate = person => async () => {
    const managers = await getAvailableManagers(person);

    return handleToggleReactivate(person, managers)();
  };

  const onReactivate = async reportTo => {
    try {
      await http.patch(api_user(user.id), {
        [STATUS]: USER_STATUSES.ACTIVE_WITHOUT_ACCESS,
        [USER_EMPLOYMENT_INFORMATION.EMPLOYMENT_TERMINATION_DATE]: null,
        [REPORT_TO]: reportTo,
      });
      showSuccessMessage(translations.reactivateUserDialog.reactivateSuccess);
    } finally {
      setPage(PEOPLE_DEFAULT_PARAMS[PAGE]);
      setUser({});
      setAvailableManagers([]);
      setIsReactivateDialogOpened(false);
      handlePageChange({
        newPage: PEOPLE_DEFAULT_PARAMS[PAGE],
        appliedFilters: selectedFilters,
        selectedUserView: selectedView,
        newOrdering: ordering,
        newSearch: search,
      });
    }
  };

  const prepareSavableFieldKeys = baseKeys =>
    canViewSensitiveDataCheck(user)
      ? baseKeys
      : baseKeys.filter(key => !fieldsSettings[key]?.is_sensitive);

  const onUpdateExternalUserInfo = async values => {
    const userData = {};
    const keys = prepareSavableFieldKeys(EXTERNAL_USER_INFO_KEYS);
    keys.forEach(key => {
      if (user[key] !== values[key]) {
        userData[key] = values[key];
      }
    });

    if (!isObjectEmpty(userData)) {
      return http.patch(api_user(user.id), userData).then(() => {
        setPage(PEOPLE_DEFAULT_PARAMS[PAGE]);
        setUser({});
        handlePageChange({
          newPage: PEOPLE_DEFAULT_PARAMS[PAGE],
          appliedFilters: selectedFilters,
          selectedUserView: selectedView,
          newOrdering: ordering,
          newSearch: search,
        });
        showSuccessMessage(translations.userEditSuccess);
      });
    }

    return Promise.resolve();
  };

  const onUpdatePersonalInfo = async values => {
    const userData = {};
    const keys = prepareSavableFieldKeys(USER_PERSONAL_INFO_KEYS);
    keys.forEach(key => {
      if (user[key] !== values[key]) {
        if (key === USER_BASIC_PERSONAL_INFORMATION.GENDER) {
          if (user[key] !== values[key]?.name) {
            userData[key] = values[key]?.name;
          }
          return;
        }
        if (
          key === USER_BASIC_PERSONAL_INFORMATION.YEARS_OF_EXPERIENCE &&
          !values[key]
        ) {
          userData[key] = null;
          return;
        }
        if (key === USER_BASIC_PERSONAL_INFORMATION.LANGUAGE) {
          if (user[key] !== values[key]?.value) {
            userData[key] = values[key]?.value;
          }
          return;
        }
        if (key === USER_BASIC_PERSONAL_INFORMATION.TIME_ZONE) {
          if (user[key] !== values[key]?.value) {
            userData[key] = values[key]?.value;
          }
          return;
        }
        userData[key] = values[key];
      }
    });

    if (!isObjectEmpty(userData)) {
      http.patch(api_user_personal_info(user.id), userData).then(async () => {
        setPage(PEOPLE_DEFAULT_PARAMS[PAGE]);
        setUser({});
        handlePageChange({
          newPage: PEOPLE_DEFAULT_PARAMS[PAGE],
          appliedFilters: selectedFilters,
          selectedUserView: selectedView,
          newOrdering: ordering,
          newSearch: search,
        });
        showSuccessMessage(translations.userEditSuccess);
        if (
          isOwnProfile &&
          (userData[USER_BASIC_PERSONAL_INFORMATION.FIRST_NAME] !==
            user[USER_BASIC_PERSONAL_INFORMATION.FIRST_NAME] ||
            userData[USER_BASIC_PERSONAL_INFORMATION.LAST_NAME] !==
              user[USER_BASIC_PERSONAL_INFORMATION.LAST_NAME])
        ) {
          dispatch(
            updateCurrentUserInfo({
              ...(userData[USER_BASIC_PERSONAL_INFORMATION.FIRST_NAME]
                ? {
                    [USER_BASIC_PERSONAL_INFORMATION.FIRST_NAME]:
                      userData[USER_BASIC_PERSONAL_INFORMATION.FIRST_NAME],
                  }
                : {}),
              ...(userData[USER_BASIC_PERSONAL_INFORMATION.LAST_NAME]
                ? {
                    [USER_BASIC_PERSONAL_INFORMATION.LAST_NAME]:
                      userData[USER_BASIC_PERSONAL_INFORMATION.LAST_NAME],
                  }
                : {}),
            })
          );
        }
      });
    }

    return Promise.resolve();
  };

  const onUpdateEmploymentInfo = async values => {
    const userData = {};
    const keys = prepareSavableFieldKeys(USER_EMPLOYMENT_INFO_KEYS);
    keys.forEach(key => {
      if (user[key] !== values[key]) {
        if (key === USER_EMPLOYMENT_INFORMATION.WORKING_HOURS && !values[key]) {
          userData[key] = null;
          return;
        }
        if (
          [
            USER_EMPLOYMENT_INFORMATION.GROSS_SALARY_1,
            USER_EMPLOYMENT_INFORMATION.GROSS_SALARY_2,
            USER_EMPLOYMENT_INFORMATION.NET_SALARY,
          ].includes(key)
        ) {
          if (!values[key]) {
            userData[key] = null;
            return;
          }
        }
        if (key === USER_EMPLOYMENT_INFORMATION.JOB_TITLE) {
          if (user[key]?.id !== values[key]) {
            userData[key] = values[key];
          }
          return;
        }
        userData[key] = values[key];
      }
    });

    if (!isObjectEmpty(userData)) {
      await http.patch(api_user_employment_info(user.id), userData);

      if (isOwnProfile && userData[USER_EMPLOYMENT_INFORMATION.JOB_TITLE]) {
        dispatch(
          updateCurrentUserInfo({
            [USER_EMPLOYMENT_INFORMATION.JOB_TITLE]: getItemById(
              jobTitles,
              userData[USER_EMPLOYMENT_INFORMATION.JOB_TITLE]
            ),
          })
        );
      }

      if (!isOwnProfile && !hasGlobalEditScope) {
        if (
          userData[USER_EMPLOYMENT_INFORMATION.REPORT_TO] &&
          (directReportsSet.has(user.id) ||
            indirectReportsSet.has(user.id) ||
            userData[USER_EMPLOYMENT_INFORMATION.REPORT_TO] === auth.id)
        ) {
          getAuthReporters(dispatch);
        }
        if (
          (customEmployeesScopeSet.has(user.id) ||
            directReportsSet.has(user.id) ||
            indirectReportsSet.has(user.id)) &&
          (userData[USER_EMPLOYMENT_INFORMATION.DIVISION] ||
            userData[USER_EMPLOYMENT_INFORMATION.DEPARTMENT] ||
            userData[USER_EMPLOYMENT_INFORMATION.FRAMEWORK] ||
            userData[USER_EMPLOYMENT_INFORMATION.TRACK] ||
            userData[USER_EMPLOYMENT_INFORMATION.LEVEL] ||
            userData[USER_EMPLOYMENT_INFORMATION.JOB_TITLE])
        ) {
          getAuthScopedUsers(dispatch);
        }
      }

      setPage(PEOPLE_DEFAULT_PARAMS[PAGE]);
      setUser({});
      handlePageChange({
        newPage: PEOPLE_DEFAULT_PARAMS[PAGE],
        appliedFilters: selectedFilters,
        selectedUserView: selectedView,
        newOrdering: ordering,
        newSearch: search,
      });
      showSuccessMessage(translations.userEditSuccess);
    }

    return Promise.resolve();
  };

  const renderCheckbox = (onChange, isChecked = false, disabled = false) => {
    return (
      <CustomCheckbox
        customRootClass={classes.checkbox}
        isChecked={isChecked}
        onChange={onChange}
        disabled={disabled}
        isControlled
      />
    );
  };

  const renderUserCheckbox = currentUser => {
    const isDisabled = isDisabledUserSelectCheck(
      people?.results,
      auth,
      accessControl,
      currentUser
    );

    return (
      <ConditionalTooltip
        customLabelClass={classes.tooltipText}
        message={prepareCheckboxTooltip(translations, currentUser, auth)}
        addTooltip={isDisabled}
        position="right"
      >
        {renderCheckbox(
          handleUserCheckboxChange(currentUser.id),
          isUserSelectedCheck(currentUser.id, checkedUsers),
          isDisabled
        )}
      </ConditionalTooltip>
    );
  };

  const renderUser = (currentUser, canAccessUserProfile) => {
    return (
      <UserAvatar
        labelClass={classes.userLabel}
        user={currentUser}
        clickableCaption={canAccessUserProfile}
        caption
        small
      />
    );
  };

  const renderUserStatus = userStatus => <UserStatus status={userStatus} />;

  const renderActions = (
    currentUser,
    userStatus,
    isCurrentUser,
    canEditUser,
    canDeleteUser
  ) => (
    <DotsMenu
      menuItems={prepareMenuItems(
        translations.menuItemLabels,
        currentUser,
        userStatus,
        canEditUser,
        canDeleteUser,
        isCurrentUser,
        handleEditExternalUserInfo,
        handlePersonalInfoEditOpen,
        handleEmploymentInfoEditOpen,
        handleToggleInvite,
        handleToggleReinvite,
        handleToggleRevokeAccess,
        handleOpenDeactivate,
        handleOpenReactivate,
        toggleDeleteUserDialog
      )}
    />
  );

  const preHeaderActions = preparePeoplePagePreHeaderActions(
    renderCheckbox(
      handleSelectAllChange,
      selectAllChecked,
      isDisabledUserSelectCheck(people.results || [], auth, accessControl)
    ),
    canEdit,
    canManageOnlySelf
  );
  const tableData = preparePeoplePageTableData(
    translations,
    auth,
    accessControl,
    people.results || [],
    canAdd,
    canEdit,
    canManageOnlySelf,
    isExternalUsersView,
    userStatuses,
    renderUser,
    renderUserCheckbox,
    renderActions,
    renderUserStatus
  );

  const handleChangeView = view => {
    const newFilters = getFilterParams(
      {
        ...selectedFilters,
        [WITHOUT_ACTION_PLANS]: isArrayEmpty(
          selectedFilters[WITHOUT_ACTION_PLANS]
        )
          ? undefined
          : selectedFilters[WITHOUT_ACTION_PLANS][0],
      },
      view
    );

    setSelectedFilters(newFilters);
    setSelectedView(view);
    setPage(PEOPLE_DEFAULT_PARAMS[PAGE]);
    if (!isActiveUserView(view)) {
      setSelectAllChecked(false);
      setCheckedUsers([]);
    }
    handlePageChange({
      newPage: PEOPLE_DEFAULT_PARAMS[PAGE],
      appliedFilters: newFilters,
      selectedUserView: view,
      newOrdering: ordering,
      newSearch: search,
    });
  };

  const renderPageTitle = () => {
    const totalUsers = people?.count;

    return (
      <Typography variant="h1">
        {totalUsers
          ? `${translations.pageTitle} (${totalUsers})`
          : `${translations.pageTitle}`}
      </Typography>
    );
  };

  return (
    <PageContainer
      {...rest}
      translations={translations}
      auth={auth}
      accessControl={accessControl}
      location={location}
      user={user}
      headerProps={{
        renderTitle: renderPageTitle,
      }}
      shouldPassProps={false}
      navigate={navigate}
      dispatch={dispatch}
      isFullWidthContent
    >
      {!isInitialLoad && (
        <div>
          <div className={classes.stickyHeader}>
            <Typography className={classes.description} variant="body2">
              {description}
            </Typography>
            <div className={classes.bulkEditAndFiltersWrapper}>
              <div className={classes.bulkEditAndFiltersItem}>
                {canEdit && !canManageOnlySelf && (
                  <SelectField
                    className={classes.selectBulkAction}
                    menuClass={classes.bulkActionMenu}
                    menuListClass={classes.bulkActionMenuList}
                    placeholder={translations.action}
                    options={bulkActionOptions}
                    isDisabled={isBulkEditDisabled}
                    onChange={handleSelectBulkAction}
                    isSearchDisabled
                    shouldRemoveLabel
                  />
                )}
                <Filters
                  translations={translations.filters}
                  disabled={isExternalUsersView}
                  selectedFilters={prepareWhitelistedFilters(
                    selectedFilters,
                    selectedView
                  )}
                  filters={pageFilters}
                  onApplyFilters={handleApplyFilters}
                />
              </div>
              <div className={classes.bulkEditAndFiltersItem}>
                <Search
                  className={classes.search}
                  placeholder={translations.search}
                  value={search}
                  onChange={onSearch}
                />
                <SelectField
                  className={classes.actionButton}
                  value={selectedView}
                  options={userViewOptions}
                  parser={{ value: 'value', label: 'name' }}
                  onChange={handleChangeView}
                  isSearchDisabled
                  shouldRemoveLabel
                />
                {canAdd && (
                  <Button
                    className={classes.addButton}
                    type="addRoundedNew"
                    onClick={handleOpenAddUsers}
                  >
                    {translations.addPeopleButton}
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div>
            {isArray(people.results) && !isArrayEmpty(people.results) && (
              <GridTable
                className={classes.table}
                translations={translations}
                initialSort={ordering}
                preHeaderActions={preHeaderActions}
                headerActions={headerActions}
                headers={headers}
                rows={tableData}
                hasLoadMore={hasNextPage(people)}
                onLoadMore={onLoadMore}
                onSort={onSort}
              />
            )}
            {isArray(people.results) && isArrayEmpty(people.results) && (
              <NotificationCard
                content={translations.noSearchResultsMessage}
                shouldFade={
                  isArray(people.results) && isArrayEmpty(people.results)
                }
                shouldFadeOutInstant
              />
            )}
            <CustomFormDrawer
              translations={translations}
              titleText={translations.editPersonalInformationTitle}
              initialData={prepareInitialUserData(
                user,
                canViewSensitiveDataCheck(user),
                fieldsSettings
              )}
              isOpened={
                !isObjectEmpty(user) && isEditPersonalInformationDialogOpened
              }
              fields={prepareEditPersonalInformationFields(
                user,
                canViewSensitiveDataCheck(user),
                fieldsSettings
              )}
              genders={GENDER_OPTIONS}
              languages={LANGUAGES}
              timezones={timezones}
              onClose={handleCloseEditPersonalInformationSidebar}
              onSave={onUpdatePersonalInfo}
              hideDelete
              hasCancelButton
              isInitialValid
            />
            <CustomFormDrawer
              translations={translations}
              titleText={translations.editPersonalInformationTitle}
              initialData={prepareInitialExternalUserData(user)}
              isOpened={
                !isObjectEmpty(user) && isEditExternalUserInfoDialogOpened
              }
              fields={prepareEditPersonalInformationFields(
                user,
                true,
                fieldsSettings
              )}
              onClose={handleCloseEditExternalUserInfoSidebar}
              onSave={onUpdateExternalUserInfo}
              hideDelete
              hasCancelButton
              isInitialValid
            />
            <CustomFormDrawer
              translations={translations}
              titleText={translations.editEmploymentInformationTitle}
              initialData={prepareInitialEmploymentUserData(
                user,
                canViewSensitiveDataCheck(user),
                tracks,
                frameworks,
                fieldsSettings
              )}
              isOpened={
                !isObjectEmpty(user) && isEditEmploymentInformationDialogOpened
              }
              fields={prepareEditEmploymentInformationFields(
                classes,
                canViewSensitiveDataCheck(user),
                fieldsSettings
              )}
              positions={jobTitles}
              frameworks={levelingFrameworks}
              tracks={levelingTracks}
              divisions={divisions}
              departments={departments}
              availableManagers={availableManagers}
              onClose={handleCloseEditEmploymentInformationSidebar}
              onSave={onUpdateEmploymentInfo}
              hideDelete
              hasCancelButton
              isInitialValid
            />
            <CustomFormDrawer
              translations={translations.addUsersTable}
              titleText={prepareAddUsersInfo(
                translations.addUsersTable.title,
                selectedAddUsersOption
              )}
              isInitialValid={isUserImport}
              isOpened={isAddUsersDrawerOpened}
              initialData={prepareAddUsersInitialValues(
                selectedAddUsersOption,
                organizationMentor,
                importedUsers
              )}
              fields={prepareAddUsersFields(
                classes,
                translations.addUsersTable.description,
                organizationMentor,
                selectedAddUsersOption,
                userRoles,
                reportToOptions,
                importedUsers,
                isUserImport,
                handleUserMentorChange
              )}
              organizationUser={organizationMentor}
              onClose={handleCloseAddUsersDrawer}
              onSave={onAddUsers}
              isFullWidth
              hasCancelButton
              hideDelete
            />
            <BulkEditDialog
              translations={translations.bulkEditDialog}
              isOpened={isBulkEditDialogOpened}
              users={checkedUsers}
              jobTitles={jobTitles}
              frameworks={levelingFrameworks}
              tracks={levelingTracks}
              divisions={divisionsWithDepartments}
              departments={departments}
              categories={categoriesWithTags}
              selectedBulkAction={selectedBulkAction}
              idleUsers={bulkIdleUsers}
              organizationUser={organizationMentor}
              onCancel={onBulkEditClose}
              onSave={onBulkEditSave}
            />
            <TransferEmployeesDialog
              translations={translations.transferEmployeesDialog}
              isOpened={isTransferUsersDialogOpened}
              currentUserId={user?.id}
              users={usersToTransfer}
              minDate={user[USER_EMPLOYMENT_INFORMATION.EMPLOYMENT_DATE]}
              organizationUser={organizationMentor}
              onClose={toggleTransferUsersDialog}
              onConfirm={onTransferUsers}
            />
            <AddUsersDialog
              translations={translations.addUsersDialog}
              isOpened={isAddUsersDialogOpened}
              isConfirmDisabled={isAddUsersDisabled}
              options={prepareAddUserOptions(
                translations.addUsersDialog.options
              )}
              selectedOption={selectedAddUsersOption}
              usersData={csvImport}
              onSelect={handleSelectAddUsersOption}
              onUpload={handleUploadUsers}
              onDeleteUpload={handleDeleteUpload}
              onCancel={handleCloseAddUsers}
              onConfirm={handleConfirmAddUsers}
            />
            <AssignUsersDialog
              translations={translations.reactivateUserDialog}
              isOpened={isReactivateDialogOpened}
              allUsers={availableManagers}
              onCancel={handleToggleReactivate()}
              onSave={onReactivate}
              shouldResetSelection
            />
            <AlertDialog
              translations={translations.inviteUserDialog}
              isOpened={isInviteDialogOpened}
              onClose={handleToggleInvite()}
              onConfirm={onInvite}
            />
            <AlertDialog
              translations={translations.reinviteUserDialog}
              isOpened={isReinviteDialogOpened}
              onClose={handleToggleReinvite()}
              onConfirm={onReinvite}
            />
            <AlertDialog
              translations={translations.revokeAccessUserDialog}
              isOpened={isRevokeAccessDialogOpened}
              onClose={handleToggleRevokeAccess()}
              onConfirm={onRevokeAccess}
            />
            <EmploymentTerminationDateDialog
              translations={translations.employmentTerminationDateDialog}
              isOpened={isDeactivateDialogOpened}
              minDate={user[USER_EMPLOYMENT_INFORMATION.EMPLOYMENT_DATE]}
              onClose={handleToggleDeactivate()}
              onConfirm={onDeactivate}
            />
            <AlertDialog
              translations={translations.deleteUserDialog}
              isOpened={isDeleteDialogOpened}
              onClose={toggleDeleteUserDialog()}
              onConfirm={onDelete}
              isWarning
            />
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default memo(PeoplePage);
